// PrivacyPage.jsx — Fretless Privacy Policy
// Replace frontend/src/pages/PrivacyPage.jsx with this file.
// Styled with Fretless design tokens. No external dependencies beyond React.
// Last updated: October 2026

import React, { useState } from "react";

const c = {
  appBg:      "var(--color-app-bg)",
  cardBg:     "var(--color-card-bg)",
  cardBorder: "var(--color-card-border)",
  innerBg:    "var(--color-inner-bg)",
  divider:    "var(--color-divider)",
  text:       "var(--color-text)",
  textDim:    "var(--color-text-dim)",
  textMuted:  "var(--color-text-muted)",
  orange:     "var(--color-orange)",
  orangeSoft: "var(--color-orange-soft)",
  orangeLine: "var(--color-orange-line)",
};

const font = {
  display: "'Fraunces', Georgia, serif",
  body:    "'Geist', -apple-system, system-ui, sans-serif",
  mono:    "'Geist Mono', ui-monospace, monospace",
};

const sections = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    content: `Fretless is operated by Freddie Gavita, trading as a sole trader based in the United Kingdom. References to "we", "us", or "our" in this policy mean Freddie Gavita trading as Fretless.

For data protection purposes, Freddie Gavita is the Data Controller of your personal data. You can contact us at any time at support@fretless.life.`,
  },
  {
    id: "what-we-collect",
    title: "2. What data we collect",
    content: `We collect and process the following categories of personal data:

Account data: your name, email address, and (if you sign in with Google) your Google profile information including profile picture.

Gig and work data: gig details you enter including dates, venues, fees, set times, dress codes, notes, and contacts associated with your gigs.

Contacts and collaborators: names, email addresses, phone numbers, and social media handles of people you add to your account (bandmates, promoters, fixers, venues).

Financial data: invoice details including amounts, client names, and payment status. We do not store payment card details — these are handled directly by Stripe.

Calendar data: if you connect Google Calendar, we store OAuth tokens to enable calendar sync. Gig events are written to your calendar on your behalf.

Usage data: information about how you use the app including pages visited, features used, and actions taken.

Technical data: IP address, browser type, device information, and session identifiers.`,
  },
  {
    id: "how-we-use",
    title: "3. How we use your data",
    content: `We use your personal data for the following purposes:

To provide the Fretless service: managing your gig schedule, generating invoices, syncing your calendar, and all other core features. Legal basis: contract performance.

To process payments: managing your subscription via Stripe. Legal basis: contract performance.

To send transactional emails: account confirmations, RSVP invitations to collaborators, and GDPR deletion confirmations. Legal basis: contract performance and legal obligation.

To improve the service: understanding how features are used to inform product development. Legal basis: legitimate interests.

To comply with legal obligations: retaining records as required by UK law. Legal basis: legal obligation.

AI-assisted features: when you use Smart Fill, social post generation, or other AI-powered features, relevant gig data (titles, venue names, notes) is sent to the Anthropic API to generate content. Legal basis: contract performance.`,
  },
  {
    id: "third-parties",
    title: "4. Third-party services and data processors",
    content: `We use the following third-party services that process personal data on our behalf:

MongoDB (database hosting): stores all user account data, gigs, invoices, setlists, and related content. Data processed in accordance with MongoDB's data processing agreement.

Railway (backend hosting): runs the Fretless backend, so your data passes through Railway's infrastructure when the app reads or writes it.

Vercel (web app hosting): serves the Fretless web app to your browser. It delivers the app's pages and does not store your gigs or calendar data.

Stripe (payments): processes subscription payments. Your payment card details are handled directly by Stripe and never stored by us. Stripe is PCI-DSS compliant. Data may be processed in the United States under standard contractual clauses.

Resend (transactional email): sends account-related and gig-related emails on our behalf. Email addresses and relevant gig content are shared only as necessary to deliver each email.

Anthropic (AI features): when you use AI-powered features, relevant gig text and venue data is sent to the Anthropic Claude API for processing. Anthropic is based in the United States. Data transfers are covered by standard contractual clauses. Anthropic's API data is not used to train models.

Google (OAuth, Calendar, Maps): we use Google OAuth for sign-in, Google Calendar API for calendar sync (if you connect it), Google Maps Platform for venue autocomplete, directions, and geocoding. Google's privacy policy governs their processing. Calendar OAuth tokens are stored securely and used only to sync your gig data.

Limited Use of Google user data: Fretless's use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements. We request only the Google Calendar permissions the feature needs: permission to view and edit calendar events, so your gigs can be written to and read back from your calendar, and read-only access to your list of calendars, so you can choose which one to sync.

Who we share Google user data with: Google Calendar data (details of events you choose to import, and the name of the calendar you select) is stored in our database and processed by our backend. We share it only with: (1) MongoDB and Railway, which host and process it on our behalf to run Fretless; (2) Anthropic, only if you use an AI feature on a gig that came from your calendar, in which case that gig's text is sent for processing and is not used to train models; and (3) Resend, only when you send an invitation email that includes gig details. We do not sell Google user data, share it with advertisers or data brokers, or use it to train generalised AI or machine-learning models. We disclose it otherwise only where required by law or to prevent fraud, abuse or security incidents. We do not allow humans to read it, except with your explicit consent, to resolve a support issue you have raised with us, or where required by law.

You can disconnect Google Calendar at any time in Settings → Integrations, which deletes the stored tokens.

Google Fonts: our app loads fonts (Fraunces, Geist) from Google Fonts at runtime, which sends your IP address and browser information to Google on each page load. We are working to self-host these fonts to remove this dependency.

OpenStreetMap: map tiles displayed in the Scene view are served by the OpenStreetMap Foundation. Your IP address and viewport coordinates are sent to OpenStreetMap when you view the map.

We do not sell your data to any third party. We do not use your data for advertising.`,
  },
  {
    id: "data-retention",
    title: "5. How long we keep your data",
    content: `We keep your personal data for as long as your account is active.

If you delete your account, we permanently delete all associated data including your profile, gigs, invoices, setlists, contacts, social posts, and calendar tokens. A confirmation email is sent when deletion is complete.

We may retain certain data for longer where required by law (for example, financial records for tax purposes).`,
  },
  {
    id: "your-rights",
    title: "6. Your rights under UK GDPR",
    content: `As a UK resident, you have the following rights regarding your personal data:

Right of access: you can request a copy of the personal data we hold about you.

Right to rectification: you can correct inaccurate data directly within the app, or contact us to request corrections.

Right to erasure: you can delete your account at any time from Settings → Account → Delete my account. This permanently removes all your data.

Right to restrict processing: you can ask us to limit how we use your data in certain circumstances.

Right to data portability: you can request your data in a structured, machine-readable format.

Right to object: you can object to processing based on legitimate interests.

Right to withdraw consent: where processing is based on consent, you can withdraw it at any time.

To exercise any of these rights, contact us at support@fretless.life. We will respond within 30 days. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.`,
  },
  {
    id: "security",
    title: "7. Security",
    content: `We take reasonable technical and organisational measures to protect your personal data against unauthorised access, loss, or disclosure. These include encrypted connections (HTTPS), secure token storage, and access controls.

No method of transmission over the internet is completely secure. If you believe your account has been compromised, contact us immediately at support@fretless.life.`,
  },
  {
    id: "cookies",
    title: "8. Cookies and session data",
    content: `We use a session cookie to keep you signed in while you use Fretless. This cookie is strictly necessary for the service to function and does not require consent under UK PECR.

We do not currently use analytics cookies, advertising cookies, or session recording tools. If this changes, we will update this policy and implement appropriate consent mechanisms before any tracking begins.`,
  },
  {
    id: "international",
    title: "9. International data transfers",
    content: `Some of our third-party processors (Stripe, Anthropic, Google) are based in or process data in the United States. Where personal data is transferred outside the UK, we ensure appropriate safeguards are in place, including standard contractual clauses approved by the UK Information Commissioner's Office.`,
  },
  {
    id: "children",
    title: "10. Children",
    content: `Fretless is not directed at children under 13. We do not knowingly collect personal data from children under 13. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.`,
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    content: `We may update this privacy policy from time to time. When we make significant changes, we will notify you by email or by a notice within the app. The date at the top of this page always reflects when the policy was last updated.`,
  },
  {
    id: "contact",
    title: "12. Contact us",
    content: `For any questions about this privacy policy or how we handle your data, contact us at:

Freddie Gavita (trading as Fretless)
support@fretless.life

For complaints, you may also contact the Information Commissioner's Office:
ico.org.uk | 0303 123 1113`,
  },
];

function Section({ section, isOpen, onToggle }) {
  return (
    <div
      style={{
        borderBottom: `1px solid ${c.divider}`,
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 0",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          gap: 16,
        }}
      >
        <span
          style={{
            fontFamily: font.body,
            fontSize: 15,
            fontWeight: 600,
            color: c.text,
            letterSpacing: "-0.01em",
          }}
        >
          {section.title}
        </span>
        <span
          style={{
            fontFamily: font.mono,
            fontSize: 16,
            color: c.orange,
            flexShrink: 0,
            transition: "transform 180ms ease",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
            display: "inline-block",
          }}
        >
          +
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            paddingBottom: 24,
          }}
        >
          {section.content.split("\n\n").map((para, i) => (
            <p
              key={i}
              style={{
                fontFamily: font.body,
                fontSize: 14.5,
                lineHeight: 1.65,
                color: para.endsWith(":") ? c.textDim : c.text,
                marginBottom: 14,
                marginTop: 0,
                fontWeight: para.endsWith(":") ? 500 : 400,
              }}
            >
              {para}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrivacyPage() {
  // Open the third-parties section by default (it carries the Google user data
  // sharing disclosure, which must be visible without a click), unless the URL
  // hash names a different section, e.g. /privacy#how-we-use.
  const [openSection, setOpenSection] = useState(() => {
    const hash = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
    return hash && sections.some((sec) => sec.id === hash) ? hash : "third-parties";
  });

  const toggle = (id) => setOpenSection(openSection === id ? null : id);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: c.appBg,
        fontFamily: font.body,
        color: c.text,
      }}
    >
      {/* Header */}
      <div
        style={{
          borderBottom: `1px solid ${c.divider}`,
          padding: "48px 24px 40px",
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 32,
          }}
        >
          <a
            href="/dashboard"
            style={{
              fontFamily: font.mono,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: c.orange,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            ← Dashboard
          </a>
        </div>

        <div
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: c.textMuted,
            marginBottom: 16,
          }}
        >
          Legal
        </div>

        <h1
          style={{
            fontFamily: font.display,
            fontSize: "clamp(32px, 5vw, 52px)",
            fontWeight: 600,
            letterSpacing: "-0.03em",
            lineHeight: 1.02,
            color: c.text,
            margin: "0 0 20px",
          }}
        >
          Privacy Policy
        </h1>

        <p
          style={{
            fontFamily: font.body,
            fontSize: 15,
            lineHeight: 1.6,
            color: c.textDim,
            margin: "0 0 24px",
            maxWidth: 560,
          }}
        >
          Fretless is built for working musicians. We collect only what we need
          to run the service, we never sell your data, and you can delete
          everything at any time.
        </p>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: c.orangeSoft,
            border: `1px solid ${c.orangeLine}`,
            borderRadius: 999,
            padding: "6px 14px",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: c.orange,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: font.mono,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: c.orange,
            }}
          >
            Last updated: October 2026
          </span>
        </div>
      </div>

      {/* Sections */}
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "0 24px 80px",
        }}
      >
        {/* Quick summary card */}
        <div
          style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: 16,
            padding: "24px 28px",
            margin: "40px 0 8px",
          }}
        >
          <div
            style={{
              fontFamily: font.mono,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: c.textMuted,
              marginBottom: 16,
            }}
          >
            The short version
          </div>
          {[
            "We collect your account info, gigs, invoices, and contacts — only what you give us.",
            "We use Stripe for payments, Anthropic for AI features, and Google for sign-in and calendar sync.",
            "We never sell your data or use it for advertising.",
            "You can delete your account and all your data at any time from Settings.",
            "We're UK-based and comply with UK GDPR.",
          ].map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: i < 4 ? 10 : 0,
              }}
            >
              <span
                style={{
                  color: c.orange,
                  fontFamily: font.mono,
                  fontSize: 13,
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                ✓
              </span>
              <span
                style={{
                  fontFamily: font.body,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: c.textDim,
                }}
              >
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* Accordion sections */}
        <div style={{ marginTop: 32 }}>
          {sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              isOpen={openSection === section.id}
              onToggle={() => toggle(section.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: `1px solid ${c.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <span
            style={{
              fontFamily: font.mono,
              fontSize: 11,
              color: c.textMuted,
              letterSpacing: "0.06em",
            }}
          >
            © 2026 Freddie Gavita trading as Fretless
          </span>
          <a
            href="mailto:support@fretless.life"
            style={{
              fontFamily: font.mono,
              fontSize: 11,
              color: c.orange,
              textDecoration: "none",
              letterSpacing: "0.06em",
            }}
          >
            support@fretless.life
          </a>
        </div>
      </div>
    </div>
  );
}
